import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { enumProductServiceType } from '@app/data-layer/enums/common/enumProductServiceType';
import { ShortCompanyManagementModel } from '@app/data-layer/models/management/management-company-short-model';
import { BillSearchModel } from '@app/data-layer/models/payments';
import { BILL_SERVICE_PRODUCT_CODES } from '@constants/bill-service-product-codes';
import { IDefaultSelect } from '@interfaces/default-select';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

interface SearchRequestObjectType {
  paidDateFrom?: string;
  paidDateTo?: string;
  productCodes?: string[];
  companyId?: number;
  searchParameter?: string;
}

@Component({
  selector: 'app-billing-history-search',
  templateUrl: './billing-history-search.component.html',
  styleUrls: ['./billing-history-search.component.scss'],
})

//TODO: replace name and reference of this component with the BillingHistorySearchComponent
//on the user side once all input components are added
export class BillingHistorySearchComponent implements OnInit {
  @Input() public companies: ShortCompanyManagementModel[];
  @Output() public search: EventEmitter<BillSearchModel> = new EventEmitter<BillSearchModel>();
  @Output() public dateRangeSelect = new EventEmitter<Date[]>();

  public searchForm: UntypedFormGroup;
  public serviceItems: IDefaultSelect[];
  public companyItems: IDefaultSelect[];
  public isMobile: boolean = false;

  private searchRequestObject: SearchRequestObjectType;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private formBuilder: FormBuilder, private genericService: GenericService, private translateService: TranslateService) {}

  ngOnInit() {
    this.serviceItems = Object.keys(enumProductServiceType)
      .filter((item: string) => Number(item) > -1)
      .map((item: any) => {
        const enumKeyName = enumProductServiceType[item];
        const serviceItem = {
          id: item,
          label: this.translateService.instant(`FORM_LABEL.PRODUCT_SERVICE.${enumKeyName}`),
          value: item,
        };
        return serviceItem;
      });

    if (this.companies?.length > 1) {
      this.companyItems = this.companies.map((company) => {
        return {
          id: company.id,
          label: company.businessName,
          value: company.id,
        };
      });
    }

    this.initSearchForm();
    this.setSearchRequestObject('', null, true);

    this.searchForm
      .get('productCodes')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), debounceTime(800))
      .subscribe((services: enumProductServiceType[]) => {
        this.onServiceSelect(services);
      });

    this.searchForm
      .get('companyId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((companyId) => {
        this.onCompanySelect(companyId);
      });

    this.genericService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  public initSearchForm() {
    this.searchForm = this.formBuilder.group({
      paidDateFrom: '',
      paidDateTo: '',
      productCodes: [],
      companyId: null,
      searchParameter: '',
    });
  }

  public onDateRangeSelect(selectedDates: Date[]) {
    const paidDateFrom = selectedDates.length > 0 ? selectedDates[0]?.toISOString() : null;
    const paidDateTo = selectedDates.length > 0 ? selectedDates[1]?.toISOString() : null;
    this.setSearchRequestObject('paidDateFrom', paidDateFrom);
    this.setSearchRequestObject('paidDateTo', paidDateTo);

    this.search.emit({
      ...this.searchRequestObject,
      paidDateFrom,
      paidDateTo,
    });
  }

  public onServiceSelect(services: enumProductServiceType[]) {
    const productCodes = this.getServicesProductCodes(services);
    this.setSearchRequestObject('productCodes', productCodes);

    this.search.emit({
      ...this.searchRequestObject,
      productCodes,
    });
  }

  public onCompanySelect(companyId: number) {
    this.setSearchRequestObject('companyId', companyId);
    this.search.emit({
      ...this.searchRequestObject,
      companyId,
    });
  }

  public onSearchSubmit(searchParameter: string) {
    this.setSearchRequestObject('searchParameter', searchParameter);
    this.search.emit({
      ...this.searchRequestObject,
      searchParameter,
    });
  }

  public onSearchClear() {
    this.setSearchRequestObject('', null, true);
    this.searchForm.reset(this.searchRequestObject);
    this.search.emit({ ...this.searchRequestObject });
  }

  private getServicesProductCodes(services: enumProductServiceType[]): string[] {
    if (services.length === 0) {
      return [];
    }

    const selectedServicesProductCodes = services
      .map((serviceType) => BILL_SERVICE_PRODUCT_CODES[serviceType])
      .reduce((acc, curr) => acc.concat(curr));

    return selectedServicesProductCodes;
  }

  private setSearchRequestObject(fieldname: string, fieldvalue: any, initialize: boolean = false) {
    if (initialize) {
      this.searchRequestObject = {
        paidDateFrom: '',
        paidDateTo: '',
        productCodes: [],
        companyId: null,
        searchParameter: '',
      };
    } else {
      this.searchRequestObject = {
        ...this.searchRequestObject,
        [fieldname]: fieldvalue,
      };
    }
  }
}
